/**
 * BPF Additional Photos gallery/lightbox — Phase 4D.9B.1.
 *
 * Reusable, caller-agnostic image carousel. Deliberately knows nothing
 * about the Memorial Designer, Builder state, resolved products, or
 * fetching — its entire public surface is two methods:
 *
 *   galleryEl.setImages([urlA, urlB, ...])  — render these images
 *   galleryEl.showStatus('some message')    — show text instead (loading/
 *                                              error/empty states)
 *
 * The caller (assets/bpf-memorial-designer.js today; a future product
 * page tomorrow) is responsible for knowing which product's images to
 * fetch and when — this element only ever renders whatever it's handed,
 * exactly once per call, with no internal caching or product concept of
 * its own. Opening/closing the surrounding modal is handled entirely by
 * the theme's existing <modal-dialog>/<modal-opener> (assets/global.js);
 * this element only owns what's inside the dialog.
 */
if (!customElements.get('bpf-additional-photos-gallery')) {
  customElements.define(
    'bpf-additional-photos-gallery',
    class BpfAdditionalPhotosGallery extends HTMLElement {
      constructor() {
        super();
        this.statusEl = this.querySelector('[data-bpf-gallery-status]');
        this.track = this.querySelector('[data-bpf-gallery-track]');
        this.counter = this.querySelector('[data-bpf-gallery-counter]');
        this.prevBtn = this.querySelector('[data-bpf-gallery-prev]');
        this.nextBtn = this.querySelector('[data-bpf-gallery-next]');
        this.images = [];
        this.currentIndex = 0;

        // Phase 4D.9B.3, Parts 12-13: off-DOM warm-up state. Never
        // populated until setImages() is actually called (i.e. only
        // after the caller has already fetched product image URLs
        // following a real "View Additional Photos" click) — nothing
        // here runs at initial Builder page load.
        this._warmedUrls = new Set();
        this._idleWarmHandle = null;

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.show(this.currentIndex - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.show(this.currentIndex + 1));

        // Desktop is explicitly allowed to add arrows/keyboard nav on top
        // of the mobile-primary swipe interaction (see _bindSwipe()
        // below) — keyboard support itself isn't viewport-gated, since
        // any keyboard user benefits from it regardless of screen size.
        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.show(this.currentIndex - 1);
          else if (event.key === 'ArrowRight') this.show(this.currentIndex + 1);
        });

        this._bindSwipe();
      }

      // Public API. Renders exactly the images given, in the given
      // order — this element never reorders, filters, or caches them.
      setImages(imageUrls) {
        this._cancelIdleWarm();
        this._warmedUrls = new Set();
        this.images = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
        this.currentIndex = 0;
        this._renderImages();
      }

      // Public API — shown instead of images when a caller's own fetch
      // failed entirely (loading / error / nothing-to-show states). Per-
      // image load failures are handled separately inside
      // _renderImages()'s own per-<img> error listener, never here.
      showStatus(message) {
        this._cancelIdleWarm();
        this.images = [];
        if (this.track) this.track.innerHTML = '';
        if (this.counter) this.counter.textContent = '';
        if (this.statusEl) {
          this.statusEl.textContent = message;
          this.statusEl.hidden = false;
        }
      }

      _renderImages() {
        if (!this.track) return;
        this.track.innerHTML = '';

        if (!this.images.length) {
          this.showStatus('No additional photos are available.');
          return;
        }

        if (this.statusEl) this.statusEl.hidden = true;

        this.images.forEach((url, index) => {
          const img = document.createElement('img');
          img.loading = 'lazy';
          img.decoding = 'async';
          img.src = this._sized(url, 1600);
          img.alt = '';
          img.className = 'bpf-additional-photos-gallery__image';
          img.hidden = index !== 0;
          // A single image failing to load must never crash the Builder
          // or block navigation to the others — just drop it from view;
          // the rest of the carousel and the close control keep working
          // normally either way.
          img.addEventListener('error', () => {
            img.hidden = true;
            img.dataset.bpfLoadFailed = 'true';
          });
          this.track.appendChild(img);
        });

        this.show(0);
        // Everything beyond the current + immediately adjacent images
        // (already warmed by show() above) is low-priority — warm it
        // in the background only once the browser is idle, one image
        // at a time, never blocking the modal from opening or the
        // current image from displaying.
        this._scheduleIdleWarm();
      }

      // Shopify CDN image URLs accept a `width` query param for on-the-
      // fly resizing — requesting a lightbox-appropriate size (rather
      // than the original, potentially much larger, master file) so
      // mobile performance doesn't degrade merely because this gallery
      // exists. Combined with loading="lazy" and only ever building this
      // markup once the gallery is actually opened (see the Builder-side
      // caller), no image bytes are fetched until the customer asks for
      // them.
      _sized(url, width) {
        if (!url) return url;
        try {
          const resolved = new URL(url, window.location.origin);
          resolved.searchParams.set('width', String(width));
          return resolved.toString();
        } catch (error) {
          return url;
        }
      }

      show(index) {
        if (!this.images.length || !this.track) return;
        const total = this.images.length;
        this.currentIndex = ((index % total) + total) % total;
        Array.from(this.track.children).forEach((img, i) => {
          img.hidden = i !== this.currentIndex;
        });
        if (this.counter) {
          this.counter.textContent = `${this.currentIndex + 1} / ${total}`;
        }
        this._preloadAdjacent();
      }

      // Phase 4D.9B.3, Parts 12-13: warms the current image plus its
      // immediate next/previous neighbors (wraparound-aware, matching
      // show()'s own nav) via an off-DOM Image() object — never touches
      // the real <img> elements in the track (those keep their existing
      // loading="lazy" markup as-is). This is what makes navigation feel
      // instant: by the time the customer taps/swipes/arrows to an
      // adjacent image, its bytes are typically already sitting in the
      // browser's HTTP cache under the exact same (Shopify CDN-resized)
      // URL the real <img> is about to request.
      _preloadAdjacent() {
        const total = this.images.length;
        if (!total) return;
        const nextIndex = (this.currentIndex + 1) % total;
        const prevIndex = (this.currentIndex - 1 + total) % total;
        this._warmImage(this.images[this.currentIndex]);
        this._warmImage(this.images[nextIndex]);
        this._warmImage(this.images[prevIndex]);
      }

      _warmImage(url) {
        if (!url || this._warmedUrls.has(url)) return;
        this._warmedUrls.add(url);
        const warmer = new Image();
        warmer.src = this._sized(url, 1600);
      }

      // Background-only, one image per idle tick — deliberately not a
      // Promise.all()/batch fetch, so it never competes meaningfully
      // with the current/adjacent images (already warmed synchronously
      // above) or with anything else the page is doing. Falls back to
      // a plain timeout on browsers without requestIdleCallback; either
      // way this only ever runs after setImages() has already been
      // called from a real gallery-open, never during initial Builder
      // page load.
      _scheduleIdleWarm() {
        this._cancelIdleWarm();
        const remaining = this.images.filter((url) => !this._warmedUrls.has(url));
        if (!remaining.length) return;
        let i = 0;
        const warmOne = () => {
          if (i >= remaining.length) {
            this._idleWarmHandle = null;
            return;
          }
          this._warmImage(remaining[i]);
          i += 1;
          this._idleWarmHandle = this._requestIdle(warmOne);
        };
        this._idleWarmHandle = this._requestIdle(warmOne);
      }

      _requestIdle(callback) {
        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
          return window.requestIdleCallback(callback, { timeout: 2000 });
        }
        return window.setTimeout(callback, 150);
      }

      _cancelIdleWarm() {
        if (this._idleWarmHandle == null) return;
        if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(this._idleWarmHandle);
        } else {
          window.clearTimeout(this._idleWarmHandle);
        }
        this._idleWarmHandle = null;
      }

      // Core mobile interaction — no third-party swipe/carousel library,
      // a plain touchstart/touchend delta-x check is enough for "swipe
      // left/right to move one image."
      _bindSwipe() {
        if (!this.track) return;
        let startX = null;
        const SWIPE_THRESHOLD_PX = 40;

        this.track.addEventListener(
          'touchstart',
          (event) => {
            startX = event.touches[0].clientX;
          },
          { passive: true }
        );

        this.track.addEventListener(
          'touchend',
          (event) => {
            if (startX === null) return;
            const deltaX = event.changedTouches[0].clientX - startX;
            if (deltaX > SWIPE_THRESHOLD_PX) this.show(this.currentIndex - 1);
            else if (deltaX < -SWIPE_THRESHOLD_PX) this.show(this.currentIndex + 1);
            startX = null;
          },
          { passive: true }
        );
      }
    }
  );
}
